import type { Tone } from './buildBrief'
import type { MemoryItem, MemoryKind } from './memoryApi'
import { calendarDaysBetween, formatWhen, parseWhen, relativeDay } from './parseWhen'

/**
 * How a record's memory rows read on the panel. Pure: rows and a clock in, words out - so every
 * sentence a person sees here ("6 days late", "asked 5 days ago, still open", "kept 3 days ago") is
 * a tested rule rather than something a component decided while rendering.
 */

export interface MemoryRowView {
  item: MemoryItem
  /** The short state beside the sentence: "6 days late", "due tomorrow", "5 days open", "kept 3 days ago". */
  state: string
  tone: Tone
  /** Where it came from, in words: "WhatsApp · 15 Sep 2026, 18:40". */
  source: string
  /** The AI proposed it and nobody has confirmed it yet. */
  suggested: boolean
  closed: boolean
}

export interface MemorySectionView {
  kind: MemoryKind
  title: string
  empty: string
  rows: MemoryRowView[]
}

const SECTIONS: Array<{ kind: MemoryKind; title: string; empty: string }> = [
  { kind: 'question', title: 'Open questions', empty: 'Nothing they asked is waiting for an answer.' },
  { kind: 'promise', title: 'Our promises', empty: 'Nothing is promised to them.' },
  { kind: 'want', title: 'What they want', empty: 'Nothing is recorded about what they are looking for.' },
  { kind: 'objection', title: 'Concerns', empty: 'No concerns recorded.' },
  { kind: 'fact', title: 'Worth knowing', empty: '' },
]

const CHANNEL: Record<string, string> = {
  followup: 'Follow-up', call: 'Call', whatsapp: 'WhatsApp', note: 'Note', email: 'Email', sms: 'SMS', person: 'Added by hand',
}

/** What "done" means in words, per kind. */
const DONE_WORD: Record<MemoryKind, string> = {
  question: 'answered', promise: 'kept', want: 'met', objection: 'resolved', fact: 'closed',
}

/** A question nobody has answered for this long is no longer just "open". */
const QUESTION_WARN_DAYS = 1
const QUESTION_DANGER_DAYS = 3

const days = (n: number) => `${n} day${n === 1 ? '' : 's'}`

/**
 * When the thing a row records happened.
 *
 * Two clocks meet in this table. A row the AI read took from the history carries `sourceAt` in the
 * ACCOUNT'S WALL CLOCK - that is how every history API here gives its times, with no zone. A row a
 * person typed has no history entry behind it; the database stamps its `sourceAt` in UTC, also with
 * no zone, so read the same way it lands 5h30 early in India ("added 08:09" at 13:39). For those
 * rows `createdAt` is the honest answer: it is UTC and says so.
 */
function happenedAt(item: MemoryItem): Date | null {
  if (item.origin === 'person') return parseWhen(item.createdAt)
  return parseWhen(item.sourceAt) ?? parseWhen(item.createdAt)
}

function sourceLine(item: MemoryItem): string {
  const parts: string[] = []
  if (item.sourceChannel) parts.push(CHANNEL[item.sourceChannel] ?? item.sourceChannel)
  const at = happenedAt(item)
  if (at) parts.push(formatWhen(at, true))
  return parts.join(' · ')
}

function rowView(item: MemoryItem, now: Date): MemoryRowView {
  const base = { item, source: sourceLine(item), suggested: item.origin === 'ai' && item.confirmedBy === null }

  if (item.status !== 'open') {
    const at = parseWhen(item.closedAt)
    const word = item.status === 'done' ? DONE_WORD[item.kind] : 'no longer applies'
    return { ...base, suggested: false, closed: true, tone: 'neutral', state: at ? `${word} ${relativeDay(at, now)}` : word }
  }

  if (item.kind === 'promise' && item.dueOn) {
    const due = parseWhen(item.dueOn)
    if (due) {
      const late = calendarDaysBetween(due, now)
      if (late > 0) return { ...base, closed: false, tone: 'danger', state: `${days(late)} late` }
      if (late === 0) return { ...base, closed: false, tone: 'warn', state: 'due today' }
      return { ...base, closed: false, tone: 'good', state: `due ${relativeDay(due, now)}` }
    }
  }

  if (item.kind === 'question') {
    const asked = happenedAt(item)
    if (asked) {
      const open = Math.max(calendarDaysBetween(asked, now), 0)
      const tone: Tone = open >= QUESTION_DANGER_DAYS ? 'danger' : open >= QUESTION_WARN_DAYS ? 'warn' : 'neutral'
      return { ...base, closed: false, tone, state: open === 0 ? 'asked today' : `${days(open)} open` }
    }
  }

  return { ...base, closed: false, tone: 'neutral', state: '' }
}

/** Sections in the order that matters before a call: what is owed first, then what they want. */
export function memorySections(items: MemoryItem[], now: Date): MemorySectionView[] {
  return SECTIONS.map((section) => {
    const rows = items
      .filter((i) => i.kind === section.kind && i.status !== 'superseded')
      .map((i) => rowView(i, now))
      // open before closed; within open, the most pressing first
      .sort((a, b) => Number(a.closed) - Number(b.closed) || toneRank(b.tone) - toneRank(a.tone) || b.item.id - a.item.id)
    return { ...section, rows }
  }).filter((section) => section.rows.length > 0 || section.empty !== '')
}

const toneRank = (tone: Tone) => (tone === 'danger' ? 3 : tone === 'warn' ? 2 : tone === 'good' ? 1 : 0)

export function openCount(items: MemoryItem[]): number {
  return items.filter((i) => i.status === 'open').length
}

/** What the AI read is shown, so it works on the list instead of rebuilding it. */
export function openItemsForAi(items: MemoryItem[]): Array<{ id: number; kind: string; text: string; dueOn: string | null }> {
  return items.filter((i) => i.status === 'open').map((i) => ({ id: i.id, kind: i.kind, text: i.text, dueOn: i.dueOn }))
}

const shorten = (value: string, max = 90) => (value.length > max ? `${value.slice(0, max - 1).trimEnd()}…` : value)

/**
 * The one thing from memory worth saying above everything else on the panel, if there is one: a
 * promise that is late, else a question that has waited. Free - it is arithmetic on rows that
 * already exist. An unconfirmed AI suggestion never leads the panel: nobody has vouched for it.
 */
export function memoryHeadline(items: MemoryItem[], now: Date): { tone: Tone; text: string } | null {
  const vouched = items.filter((i) => i.status === 'open' && (i.origin === 'person' || i.confirmedBy !== null))

  const latePromises = vouched
    .filter((i) => i.kind === 'promise' && i.dueOn)
    .map((i) => ({ i, late: calendarDaysBetween(parseWhen(i.dueOn) ?? now, now) }))
    .filter((x) => x.late > 0)
    .sort((a, b) => b.late - a.late)
  if (latePromises.length > 0) {
    const { i, late } = latePromises[0]
    const more = latePromises.length > 1 ? ` (+${latePromises.length - 1} more late)` : ''
    return { tone: 'danger', text: `We promised: "${shorten(i.text)}" — ${days(late)} late${more}.` }
  }

  const waiting = vouched
    .filter((i) => i.kind === 'question')
    .map((i) => ({ i, open: calendarDaysBetween(happenedAt(i) ?? now, now) }))
    .filter((x) => x.open >= QUESTION_WARN_DAYS)
    .sort((a, b) => b.open - a.open)
  if (waiting.length > 0) {
    const { i, open } = waiting[0]
    return { tone: open >= QUESTION_DANGER_DAYS ? 'danger' : 'warn', text: `They asked ${days(open)} ago and it is still open: "${shorten(i.text)}"` }
  }

  return null
}
